/**
 * Bucket — one supplier's pending-documents bucket.
 *
 * Header shows: chevron, supplier name, doc count, and a primary right-side
 * button that flips between Importieren (when bucket has docs) and Entfernen
 * (when empty — removes the supplier from the active selection).
 *
 * The whole bucket is also a drop target: file drops upload + assign to this
 * supplier; doc-row drops (custom MIME) reassign existing docs to this bucket.
 */

import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { IconChevronRight, IconUpload } from "@tabler/icons-react"
import clsx from "clsx"
import type { DragEvent } from "react"
import { useState } from "react"
import type { PendingDocumentOut } from "../../../types"
import { DOC_DRAG_MIME, PendingDocumentRow } from "./PendingDocumentRow"

interface Props {
  recordName: string
  recordPk: string | null
  docs: PendingDocumentOut[]
  downloadUrl: (docId: string) => string
  onImport: () => void
  onRemoveFromSelection: () => void
  onAcceptFiles: (files: File[]) => void
  onAcceptDocReassign: (docId: string) => void
  onDeleteDoc: (docId: string) => void
  isImporting: boolean
  newDocumentIds: Set<string>
}

export function Bucket({
  recordName,
  recordPk,
  docs,
  downloadUrl,
  onImport,
  onRemoveFromSelection,
  onAcceptFiles,
  onAcceptDocReassign,
  onDeleteDoc,
  isImporting,
  newDocumentIds,
}: Props) {
  const { translate } = useTranslation()
  const [expanded, setExpanded] = useState(true)
  const [isDragOver, setIsDragOver] = useState(false)

  const isEmpty = docs.length === 0

  const handleDragOver = (e: DragEvent) => {
    if (
      e.dataTransfer.types.includes(DOC_DRAG_MIME) ||
      e.dataTransfer.types.includes("Files")
    ) {
      e.preventDefault()
      e.dataTransfer.dropEffect = "move"
      setIsDragOver(true)
    }
  }

  const handleDragLeave = () => setIsDragOver(false)

  const handleDrop = (e: DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    const docId = e.dataTransfer.getData(DOC_DRAG_MIME)
    if (docId) {
      onAcceptDocReassign(docId)
      return
    }
    const files = Array.from(e.dataTransfer.files ?? [])
    if (files.length > 0) {
      onAcceptFiles(files)
    }
  }

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={clsx(
        "rounded-lg border bg-grey1 transition-colors",
        isDragOver
          ? "border-yellow1 bg-yellow1/5"
          : docs.some((d) => newDocumentIds.has(d.id))
            ? "border-yellow1/40"
            : "border-grey4",
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2.5">
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          disabled={isEmpty}
        >
          <IconChevronRight
            size={14}
            className={clsx(
              "shrink-0 text-grey5 transition-transform",
              expanded && !isEmpty && "rotate-90",
              isEmpty && "invisible",
            )}
          />
          <span
            className="truncate text-sm font-medium text-white"
            title={recordName}
          >
            {recordName}
          </span>
          {recordPk && (
            <span className="font-mono shrink-0 text-[11px] text-grey5">
              {recordPk}
            </span>
          )}
          <span className="shrink-0 text-xs text-grey5">
            {isEmpty
              ? translate("QMS/SUPPLIER/NO_DOCUMENTS")
              : translate("QMS/SUPPLIER/DOCUMENT_COUNT", {
                  count: docs.length,
                  documentLabel:
                    docs.length === 1
                      ? translate("QMS/SUPPLIER/DOCUMENT_SINGULAR")
                      : translate("QMS/SUPPLIER/DOCUMENT_PLURAL"),
                })}
          </span>
        </button>
        {isEmpty ? (
          <button
            onClick={onRemoveFromSelection}
            className="shrink-0 rounded-md border border-grey4 px-3 py-1.5 text-xs text-grey5 hover:border-grey5 hover:text-white"
            title={translate("QMS/SUPPLIER/REMOVE_FROM_SELECTION")}
          >
            {translate("QMS/SUPPLIER/REMOVE")}
          </button>
        ) : (
          <button
            onClick={onImport}
            disabled={isImporting}
            className="shrink-0 rounded-md bg-yellow1 px-3 py-1.5 text-xs font-medium text-black hover:bg-yellow5/80 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {isImporting
              ? translate("QMS/SUPPLIER/PROCESSING")
              : translate("QMS/SUPPLIER/IMPORT")}
          </button>
        )}
      </div>

      {/* Body */}
      {expanded && !isEmpty && (
        <div>
          {docs.map((doc) => (
            <PendingDocumentRow
              key={doc.id}
              doc={doc}
              downloadUrl={downloadUrl(doc.id)}
              onDelete={() => onDeleteDoc(doc.id)}
              isNew={newDocumentIds.has(doc.id)}
            />
          ))}
        </div>
      )}

      {isDragOver && isEmpty && (
        <div className="border-t border-yellow1/40 px-3 py-2 text-center text-xs text-yellow1">
          <IconUpload size={14} className="mr-1 inline" />
          {translate("QMS/SUPPLIER/DROP_HERE")}
        </div>
      )}
    </div>
  )
}
